/**
 * TerminalPane
 *
 * A deliberately thin React wrapper over `SessionTerminal`, the class that
 * already owns xterm and the attachment to the VM's serial console. The class
 * is the source of truth; this component's only jobs are to give it a DOM
 * node, forward its two callbacks, and tear it down exactly once.
 *
 * The one rule that matters: the terminal is the learner's shell session. It
 * must survive every re-render and every tab switch, so the effect that builds
 * it depends on `sessionId` and nothing else. Callbacks are read through refs
 * so that a parent re-rendering with fresh closures never re-attaches.
 */
import { useEffect, useImperativeHandle, useRef, type Ref } from 'react';
import { registerTerminalReader } from '../../lib/terminal-tail';
import { SessionTerminal, type TerminalLocations } from '../../terminal';
import type { TerminalPaneProps } from '../contracts';
import { PhoneKeys } from './PhoneKeys';

/** What a host (WorkPane) can ask the terminal to do imperatively. */
export interface TerminalHandle {
  /** Move keyboard focus into the shell. */
  focus(): void;
  /** Re-measure after the pane was revealed; xterm cannot fit while hidden. */
  refit(): void;
  /**
   * Type a line at the prompt and press Enter, as the learner would.
   * `busy` while something is still running; `detached` with no shell.
   */
  type(line: string): 'typed' | 'busy' | 'detached';
  /** The line the cursor is on: at an idle shell, the prompt. */
  cursorLine(): string;
}

/**
 * Implements `TerminalPaneProps`. The optional `ref` is React 19's plain ref
 * prop — WorkPane owns it internally, so callers pass only the contract props.
 */
export function TerminalPane({
  sessionId,
  onGitState,
  onStatus,
  onCommandResult,
  locations,
  reconnectKey,
  ref,
}: TerminalPaneProps & { ref?: Ref<TerminalHandle>; locations?: TerminalLocations }) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const termRef = useRef<SessionTerminal | null>(null);

  // Latest callbacks, so the terminal effect can stay keyed on sessionId only.
  const gitRef = useRef(onGitState);
  const statusRef = useRef(onStatus);
  const resultRef = useRef(onCommandResult);
  const locationsRef = useRef(locations);
  useEffect(() => {
    gitRef.current = onGitState;
    statusRef.current = onStatus;
    resultRef.current = onCommandResult;
    locationsRef.current = locations;
  });

  useImperativeHandle(
    ref,
    () => ({
      focus: () => termRef.current?.focus(),
      refit: () => termRef.current?.refit(),
      type: (line) => termRef.current?.type(line) ?? 'detached',
      cursorLine: () => termRef.current?.cursorLine() ?? '',
    }),
    [],
  );

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const term = new SessionTerminal(
      host,
      (state) => gitRef.current(state),
      (text) => statusRef.current(text),
      (ok) => resultRef.current?.(ok),
      {
        resolve: (path) => locationsRef.current?.resolve(path) ?? null,
        open: (path, line, col) => locationsRef.current?.open(path, line, col),
      },
    );
    termRef.current = term;
    term.connect(sessionId);
    // What the mentor reads when a question is asked (useSession, `askModel`).
    // Registered with the terminal and gone with it, so a question never
    // reads a terminal that has been torn down.
    const unregister = registerTerminalReader(sessionId, () => term.recentText());

    return () => {
      unregister();
      termRef.current = null;
      // Detaches from ttyS0 and disposes xterm, so strict mode's second mount
      // starts from an empty container rather than stacking a second terminal.
      // The guest's shell keeps running; only the view goes away.
      term.dispose();
    };
  }, [sessionId]);

  // `reconnectKey` is a signal, not state: only a *change* means "reconnect".
  // A fresh sessionId already reconnected in the effect above, so skip that.
  const seen = useRef<{ id: string; key: number } | null>(null);
  useEffect(() => {
    const previous = seen.current;
    seen.current = { id: sessionId, key: reconnectKey };
    if (!previous) return;
    if (previous.id !== sessionId) return;
    if (previous.key === reconnectKey) return;
    termRef.current?.reconnect();
  }, [sessionId, reconnectKey]);

  // The key bar is drawn always and shown only in the phone layout
  // (play.css), so turning a phone sideways never remounts the terminal.
  return (
    <>
      <div className="term" ref={hostRef} />
      <PhoneKeys onKey={(bytes) => termRef.current?.send(bytes)} />
    </>
  );
}

export default TerminalPane;
